import React, { useMemo } from "react";
import styled from "styled-components";
import PropTypes from "prop-types";

import { amountToCents, centsToAmount, formatCurrency } from "../utils";
import Text from "./Text";
import CartItemImage from "./CartItemImage";

const Wrap = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  background: transparent;
  position: relative;
`;

const ImageWrapper = styled.div`
  width: 56px;
  height: 56px;
  flex-shrink: 0;
  margin-right: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const InfoBlock = styled.div`
  flex: 1;
  gap: 4px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-width: 0;
`;

const NameText = styled(Text)`
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CartItemInfo = styled.div`
  display: flex;
  justify-content: space-between;
`;

const CartItem = ({
  item,
  className,
  shopper,
  service,
  withImage,
  currency = "EUR",
  locale = "es-ES",
}) => {
  const { name, image_urls, price_with_tax, quantity, reference } = item;
  const price = centsToAmount(price_with_tax);
  const [imageUrl] = image_urls || [];
  const singleItem = quantity === 1 || service;

  const showImage = withImage && !service;

  const priceNumber = useMemo(() => amountToCents(price) / 100, [price]);

  return (
    <Wrap
      data-testid="cartitem"
      $singleItem={singleItem}
      $withImage={showImage}
      $isZeroPrice={!amountToCents(price)}
      className={className}
    >
      {showImage && imageUrl ? (
        <ImageWrapper>
          <CartItemImage image={imageUrl} name={name} />
        </ImageWrapper>
      ) : null}
      <InfoBlock>
        <NameText primary body2 ellipsis="2">
          {name}
        </NameText>
        <CartItemInfo>
          {shopper ? (
            <Text body2 secondary>
              {`${shopper.quantity} ${shopper.text}`}
            </Text>
          ) : (
            <Text body2 secondary>
              {reference}
            </Text>
          )}
          <Text body2 primary right>
            {formatCurrency(priceNumber, currency, locale)}
          </Text>
        </CartItemInfo>
      </InfoBlock>
    </Wrap>
  );
};

CartItem.propTypes = {
  item: PropTypes.object,
  className: PropTypes.string,
  shopper: PropTypes.shape({
    quantity: PropTypes.number,
    text: PropTypes.string,
  }),
  service: PropTypes.bool,
  withImage: PropTypes.bool,
  currency: PropTypes.string,
  locale: PropTypes.string,
};

CartItem.defaultProps = {
  item: {},
  className: "",
  shopper: null,
  service: false,
  withImage: true,
  currency: "€",
  locale: "es-ES",
};

export default CartItem;
